import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  HelpCircle,
  MoreVertical,
  FolderOpen,
  Eye,
  EyeOff,
  GripVertical,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetFAQCategoriesQuery,
  useCreateFAQCategoryMutation,
  useUpdateFAQCategoryMutation,
  useDeleteFAQCategoryMutation,
  useGetAllFAQsQuery,
  useCreateFAQMutation,
  useUpdateFAQMutation,
  useDeleteFAQMutation,
} from "@/store";
import type {
  FAQCategory,
  FAQ,
  CreateFAQCategoryData,
  UpdateFAQCategoryData,
  CreateFAQData,
  UpdateFAQData,
} from "@/store/faqApi";
import { TablePagination } from "@/components/admin";
import { toast } from "sonner";

const DEFAULT_PAGE_SIZE = 20;

// Helper to generate slug from name
const generateSlug = (name: string) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
};

export function AdminFAQScreen() {
  const [activeTab, setActiveTab] = useState("faqs");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  // Pagination state
  const [faqPage, setFaqPage] = useState(1);
  const [faqLimit, setFaqLimit] = useState(DEFAULT_PAGE_SIZE);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryLimit, setCategoryLimit] = useState(DEFAULT_PAGE_SIZE);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setFaqPage(1);
      setCategoryPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset FAQ page when category filter changes
  useEffect(() => {
    setFaqPage(1);
  }, [categoryFilter]);

  // Category state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<FAQCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<FAQCategory | null>(null);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState<CreateFAQCategoryData>({
    name: "",
    slug: "",
    description: "",
    isActive: true,
  });

  // FAQ state
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [faqToEdit, setFaqToEdit] = useState<FAQ | null>(null);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);
  const [deleteFaqDialogOpen, setDeleteFaqDialogOpen] = useState(false);
  const [faqFormData, setFaqFormData] = useState<CreateFAQData>({
    question: "",
    answer: "",
    categoryId: "",
    isActive: true,
  });

  // API hooks for categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetFAQCategoriesQuery({
    page: categoryPage,
    limit: categoryLimit,
  });
  const { data: filterCategoriesData } = useGetFAQCategoriesQuery(
    { page: 1, limit: 500 },
    { skip: activeTab !== "faqs" }
  );
  const filterCategories = filterCategoriesData?.data ?? [];
  const [createCategory, { isLoading: isCreatingCategory }] = useCreateFAQCategoryMutation();
  const [updateCategory, { isLoading: isUpdatingCategory }] = useUpdateFAQCategoryMutation();
  const [deleteCategory, { isLoading: isDeletingCategory }] = useDeleteFAQCategoryMutation();

  // API hooks for FAQs
  const { data: faqsData, isLoading: isLoadingFAQs } = useGetAllFAQsQuery({
    page: faqPage,
    limit: faqLimit,
    search: debouncedSearch || undefined,
  });
  const [createFAQ, { isLoading: isCreatingFAQ }] = useCreateFAQMutation();
  const [updateFAQ, { isLoading: isUpdatingFAQ }] = useUpdateFAQMutation();
  const [deleteFAQ, { isLoading: isDeletingFAQ }] = useDeleteFAQMutation();

  const categories = categoriesData?.data || [];
  const faqs = faqsData?.data || [];
  
  const faqPagination = faqsData?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };
  
  const categoryPagination = categoriesData?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  // Filter categories based on search (client-side since backend doesn't filter categories by search)
  const filteredCategories = categories.filter(
    (cat) =>
      !debouncedSearch ||
      cat.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      cat.slug.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  // FAQs are filtered server-side, so we use them directly
  const filteredFAQs = faqs;

  // ==================== CATEGORY HANDLERS ====================

  const openCreateCategoryDialog = () => {
    setCategoryToEdit(null);
    setCategoryFormData({
      name: "",
      slug: "",
      description: "",
      isActive: true,
    });
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (category: FAQCategory) => {
    setCategoryToEdit(category);
    setCategoryFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || "",
      isActive: category.isActive,
    });
    setCategoryDialogOpen(true);
  };

  const handleCategoryNameChange = (name: string) => {
    setCategoryFormData({
      ...categoryFormData,
      name,
      slug: categoryToEdit ? categoryFormData.slug : generateSlug(name),
    });
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    if (!categoryFormData.slug.trim()) {
      toast.error("Category slug is required");
      return;
    }

    try {
      if (categoryToEdit) {
        const updateData: UpdateFAQCategoryData = {
          name: categoryFormData.name,
          slug: categoryFormData.slug,
          description: categoryFormData.description,
          isActive: categoryFormData.isActive,
        };
        await updateCategory({ id: categoryToEdit.id, data: updateData }).unwrap();
        toast.success("Category updated successfully");
      } else {
        await createCategory(categoryFormData).unwrap();
        toast.success("Category created successfully");
      }
      setCategoryDialogOpen(false);
      setCategoryToEdit(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to save category";
      toast.error(errorMessage);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategory(categoryToDelete.id).unwrap();
      toast.success("Category deleted successfully");
      setDeleteCategoryDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to delete category";
      toast.error(errorMessage);
    }
  };

  const handleToggleCategoryStatus = async (category: FAQCategory) => {
    try {
      await updateCategory({
        id: category.id,
        data: { isActive: !category.isActive },
      }).unwrap();
      toast.success(`Category ${category.isActive ? "deactivated" : "activated"} successfully`);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to update category";
      toast.error(errorMessage);
    }
  };

  // ==================== FAQ HANDLERS ====================

  const openCreateFAQDialog = () => {
    setFaqToEdit(null);
    setFaqFormData({
      question: "",
      answer: "",
      categoryId: categories[0]?.id || "",
      isActive: true,
    });
    setFaqDialogOpen(true);
  };

  const openEditFAQDialog = (faq: FAQ) => {
    setFaqToEdit(faq);
    setFaqFormData({
      question: faq.question,
      answer: faq.answer,
      categoryId: faq.categoryId,
      isActive: faq.isActive,
    });
    setFaqDialogOpen(true);
  };

  const handleSaveFAQ = async () => {
    if (!faqFormData.question.trim()) {
      toast.error("Question is required");
      return;
    }

    if (!faqFormData.answer.trim()) {
      toast.error("Answer is required");
      return;
    }

    if (!faqFormData.categoryId) {
      toast.error("Please select a category");
      return;
    }

    try {
      if (faqToEdit) {
        const updateData: UpdateFAQData = {
          question: faqFormData.question,
          answer: faqFormData.answer,
          categoryId: faqFormData.categoryId,
          isActive: faqFormData.isActive,
        };
        await updateFAQ({ id: faqToEdit.id, data: updateData }).unwrap();
        toast.success("FAQ updated successfully");
      } else {
        await createFAQ(faqFormData).unwrap();
        toast.success("FAQ created successfully");
      }
      setFaqDialogOpen(false);
      setFaqToEdit(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to save FAQ";
      toast.error(errorMessage);
    }
  };

  const handleDeleteFAQ = async () => {
    if (!faqToDelete) return;

    try {
      await deleteFAQ(faqToDelete.id).unwrap();
      toast.success("FAQ deleted successfully");
      setDeleteFaqDialogOpen(false);
      setFaqToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to delete FAQ";
      toast.error(errorMessage);
    }
  };

  const handleToggleFAQStatus = async (faq: FAQ) => {
    try {
      await updateFAQ({
        id: faq.id,
        data: { isActive: !faq.isActive },
      }).unwrap();
      toast.success(`FAQ ${faq.isActive ? "deactivated" : "activated"} successfully`);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to update FAQ";
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FAQ Management</h1>
          <p className="text-muted-foreground">
            Manage frequently asked questions and categories
          </p>
        </div>
        <Button
          onClick={activeTab === "categories" ? openCreateCategoryDialog : openCreateFAQDialog}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          {activeTab === "categories" ? "Add Category" : "Add FAQ"}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
          <CardDescription>
            Find {activeTab === "categories" ? "categories" : "FAQs"} by keyword
            {activeTab === "faqs" && " and filter by category"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {activeTab === "faqs" && (
            <div className="flex flex-col gap-2 max-w-md">
              <Label htmlFor="faq-category-filter">Filter by category</Label>
              <Select
                value={categoryFilter || "all"}
                onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}
              >
                <SelectTrigger id="faq-category-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {filterCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="faqs" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            FAQs ({faqPagination.total})
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <FolderOpen className="h-4 w-4" />
            Categories ({categoryPagination.total})
          </TabsTrigger>
        </TabsList>

        {/* FAQs Tab */}
        <TabsContent value="faqs">
          <Card>
            <CardHeader>
              <CardTitle>All FAQs</CardTitle>
              <CardDescription>
                {faqPagination.total} {faqPagination.total === 1 ? "FAQ" : "FAQs"} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingFAQs ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading FAQs...
                </div>
              ) : filteredFAQs.length === 0 ? (
                <div className="text-center py-12">
                  <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No FAQs found</p>
                  {categories.length > 0 ? (
                    <Button onClick={openCreateFAQDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First FAQ
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Create a category first before adding FAQs
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Question</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Updated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredFAQs.map((faq) => (
                          <TableRow key={faq.id}>
                            <TableCell>
                              <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                            </TableCell>
                            <TableCell>
                              <div className="max-w-md">
                                <p className="font-medium line-clamp-1">{faq.question}</p>
                                <p className="text-sm text-muted-foreground line-clamp-1">
                                  {faq.answer}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{faq.category?.name || "Unknown"}</Badge>
                            </TableCell>
                            <TableCell>
                              {faq.isActive ? (
                                <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                                  <Eye className="h-3 w-3" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="gap-1">
                                  <EyeOff className="h-3 w-3" />
                                  Hidden
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(faq.updatedAt)}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditFAQDialog(faq)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleToggleFAQStatus(faq)}>
                                    {faq.isActive ? (
                                      <>
                                        <EyeOff className="h-4 w-4 mr-2" />
                                        Hide
                                      </>
                                    ) : (
                                      <>
                                        <Eye className="h-4 w-4 mr-2" />
                                        Show
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setFaqToDelete(faq);
                                      setDeleteFaqDialogOpen(true);
                                    }}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* FAQ Pagination */}
                  <TablePagination
                    pagination={faqPagination}
                    onPageChange={setFaqPage}
                    onLimitChange={(newLimit) => {
                      setFaqLimit(newLimit);
                      setFaqPage(1);
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <CardTitle>All Categories</CardTitle>
              <CardDescription>
                {filteredCategories.length}{" "}
                {filteredCategories.length === 1 ? "category" : "categories"} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingCategories ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading categories...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No categories found</p>
                  <Button onClick={openCreateCategoryDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Category
                  </Button>
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Slug</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Updated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCategories.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell>
                              <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                            </TableCell>
                            <TableCell className="font-medium">{category.name}</TableCell>
                            <TableCell>
                              <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                                {category.slug}
                              </code>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {category.description || "—"}
                              </p>
                            </TableCell>
                            <TableCell>
                              {category.isActive ? (
                                <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                                  <Eye className="h-3 w-3" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="gap-1">
                                  <EyeOff className="h-3 w-3" />
                                  Hidden
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(category.updatedAt)}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditCategoryDialog(category)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleToggleCategoryStatus(category)}>
                                    {category.isActive ? (
                                      <>
                                        <EyeOff className="h-4 w-4 mr-2" />
                                        Hide
                                      </>
                                    ) : (
                                      <>
                                        <Eye className="h-4 w-4 mr-2" />
                                        Show
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setCategoryToDelete(category);
                                      setDeleteCategoryDialogOpen(true);
                                    }}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Category Pagination */}
                  <TablePagination
                    pagination={categoryPagination}
                    onPageChange={setCategoryPage}
                    onLimitChange={(newLimit) => {
                      setCategoryLimit(newLimit);
                      setCategoryPage(1);
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {categoryToEdit ? "Edit Category" : "Create Category"}
            </DialogTitle>
            <DialogDescription>
              {categoryToEdit
                ? "Update the FAQ category details."
                : "Add a new category for organizing FAQs."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="category-name">Name *</Label>
              <Input
                id="category-name"
                placeholder="e.g., Ordering"
                value={categoryFormData.name}
                onChange={(e) => handleCategoryNameChange(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category-slug">Slug *</Label>
              <Input
                id="category-slug"
                placeholder="e.g., ordering"
                value={categoryFormData.slug}
                onChange={(e) =>
                  setCategoryFormData({ ...categoryFormData, slug: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                URL-friendly identifier. Auto-generated from name.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                placeholder="Brief description of this category..."
                value={categoryFormData.description}
                onChange={(e) =>
                  setCategoryFormData({ ...categoryFormData, description: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">
                  Show this category on the public FAQ page
                </p>
              </div>
              <Switch
                checked={categoryFormData.isActive}
                onCheckedChange={(checked) =>
                  setCategoryFormData({ ...categoryFormData, isActive: checked })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCategoryDialogOpen(false)}
              disabled={isCreatingCategory || isUpdatingCategory}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveCategory}
              disabled={isCreatingCategory || isUpdatingCategory}
            >
              {isCreatingCategory || isUpdatingCategory
                ? "Saving..."
                : categoryToEdit
                ? "Save Changes"
                : "Create Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={faqDialogOpen} onOpenChange={setFaqDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-4 sm:max-w-2xl">
          <DialogHeader className="shrink-0">
            <DialogTitle>{faqToEdit ? "Edit FAQ" : "Create FAQ"}</DialogTitle>
            <DialogDescription>
              {faqToEdit
                ? "Update the frequently asked question."
                : "Add a new frequently asked question."}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="faq-category">Category *</Label>
              <Select
                value={faqFormData.categoryId}
                onValueChange={(value) =>
                  setFaqFormData({ ...faqFormData, categoryId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="faq-question">Question *</Label>
              <Textarea
                id="faq-question"
                placeholder="e.g., How do I track my order?"
                value={faqFormData.question}
                onChange={(e) =>
                  setFaqFormData({ ...faqFormData, question: e.target.value })
                }
                rows={2}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="faq-answer">Answer *</Label>
              <Textarea
                id="faq-answer"
                placeholder="Provide a detailed answer to the question..."
                value={faqFormData.answer}
                onChange={(e) =>
                  setFaqFormData({ ...faqFormData, answer: e.target.value })
                }
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                Supports Markdown: use <code className="bg-muted px-1 rounded">- </code> or{" "}
                <code className="bg-muted px-1 rounded">* </code> for bullets,{" "}
                <code className="bg-muted px-1 rounded">1. </code> for numbered lists.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">
                  Show this FAQ on the public page
                </p>
              </div>
              <Switch
                checked={faqFormData.isActive}
                onCheckedChange={(checked) =>
                  setFaqFormData({ ...faqFormData, isActive: checked })
                }
              />
            </div>
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button
              variant="outline"
              onClick={() => setFaqDialogOpen(false)}
              disabled={isCreatingFAQ || isUpdatingFAQ}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFAQ}
              disabled={isCreatingFAQ || isUpdatingFAQ}
            >
              {isCreatingFAQ || isUpdatingFAQ
                ? "Saving..."
                : faqToEdit
                ? "Save Changes"
                : "Create FAQ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <AlertDialog
        open={deleteCategoryDialogOpen}
        onOpenChange={setDeleteCategoryDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the category "{categoryToDelete?.name}" and
              all FAQs within it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setCategoryToDelete(null);
                setDeleteCategoryDialogOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              disabled={isDeletingCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingCategory ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete FAQ Confirmation */}
      <AlertDialog open={deleteFaqDialogOpen} onOpenChange={setDeleteFaqDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this FAQ. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setFaqToDelete(null);
                setDeleteFaqDialogOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFAQ}
              disabled={isDeletingFAQ}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingFAQ ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
